import { useEffect, useMemo, useState } from "react";
import api, { eur, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { FileText, Upload, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const IGNORE = "__ignore__";

export default function Invoices() {
  const [ingredients, setIngredients] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [rows, setRows] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    api.get("/ingredients").then((r) => setIngredients(r.data));
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    setRows(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/invoices/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSupplier(data.supplier || "");
      setRows(
        data.lines.map((l) => ({
          ...l,
          target: l.suggested_ingredient_id || IGNORE,
          new_cost: l.total ?? l.unit_price ?? 0,
          new_size: l.quantity ?? "",
        }))
      );
      if (data.lines.length === 0) toast.warning("Nessuna riga trovata nella fattura");
      else toast.success(`${data.lines.length} righe importate`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore lettura XML");
    } finally {
      setParsing(false);
      e.target.value = "";
    }
  };

  const updateRow = (idx, key, val) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [key]: val } : r)));

  const apply = async () => {
    const items = (rows || [])
      .filter((r) => r.target && r.target !== IGNORE)
      .map((r) => ({
        ingredient_id: r.target,
        package_cost: Number(r.new_cost) || 0,
        package_size: r.new_size ? Number(r.new_size) : null,
        supplier_code: r.code || null,
        supplier: supplier || null,
      }));
    if (items.length === 0) return toast.error("Collega almeno una riga a un ingrediente");
    setApplying(true);
    try {
      const { data } = await api.post("/invoices/apply", { items });
      toast.success(`${data.updated} ingredienti aggiornati`);
      setRows(null);
      api.get("/ingredients").then((r) => setIngredients(r.data));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    } finally {
      setApplying(false);
    }
  };

  const mappedCount = useMemo(
    () => (rows || []).filter((r) => r.target && r.target !== IGNORE).length,
    [rows]
  );

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Fatture XML</h1>
        <p className="text-muted-foreground mt-1">
          Importa le fatture elettroniche (FatturaPA) per aggiornare i costi delle materie prime
        </p>
      </div>

      {!rows && (
        <Card className="p-10 border-dashed border-2 text-center">
          <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
            <FileText className="w-7 h-7 text-primary" />
          </div>
          <p className="font-medium mb-1">Carica una fattura elettronica</p>
          <p className="text-sm text-muted-foreground mb-5">Formato XML FatturaPA</p>
          <label>
            <input type="file" accept=".xml,text/xml" className="hidden" onChange={handleFile} data-testid="invoice-file-input" />
            <Button asChild className="rounded-full gap-2" disabled={parsing}>
              <span>
                {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {parsing ? "Analisi in corso..." : "Seleziona file XML"}
              </span>
            </Button>
          </label>
        </Card>
      )}

      {rows && (
        <>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              {supplier && <p className="text-sm">Fornitore: <strong>{supplier}</strong></p>}
              <p className="text-sm text-muted-foreground">
                {mappedCount} di {rows.length} righe collegate a un ingrediente
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setRows(null)}>Annulla</Button>
              <Button onClick={apply} disabled={applying} className="rounded-full gap-2" data-testid="apply-invoice-btn">
                {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Aggiorna costi
              </Button>
            </div>
          </div>

          <Card className="border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wider">Descrizione fattura</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Q.tà</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Importo</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider w-[240px]">Collega a ingrediente</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Nuovo costo €</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider text-right">Nuova q.tà</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={idx} className="hover:bg-secondary/40" data-testid={`invoice-row-${idx}`}>
                    <TableCell className="max-w-[260px]">
                      <p className="font-medium text-sm truncate">{r.description}</p>
                      {r.code && <p className="text-[11px] text-muted-foreground font-mono">cod. {r.code}</p>}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{r.quantity ?? "—"}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{eur(r.total ?? r.unit_price ?? 0)}</TableCell>
                    <TableCell>
                      <Select value={r.target} onValueChange={(v) => updateRow(idx, "target", v)}>
                        <SelectTrigger className="h-9" data-testid={`invoice-map-${idx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={IGNORE}>Ignora questa riga</SelectItem>
                          {ingredients.map((i) => (
                            <SelectItem key={i.id} value={i.id}>
                              {i.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        value={r.new_cost}
                        onChange={(e) => updateRow(idx, "new_cost", e.target.value)}
                        className="h-9 w-24 ml-auto"
                        disabled={r.target === IGNORE}
                        data-testid={`invoice-cost-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={r.new_size}
                        onChange={(e) => updateRow(idx, "new_size", e.target.value)}
                        className="h-9 w-24 ml-auto"
                        disabled={r.target === IGNORE}
                        placeholder="opz."
                        data-testid={`invoice-size-${idx}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}
